import { useTranslation } from 'react-i18next';
import { useState, type FC } from 'react';
import { SMS_ACTIVITIES_PER_PAGE } from 'infrastructure/hooks/ccm';
import BaseTableNew from 'components/atoms/base-table/new';
import BaseButton from 'components/atoms/baseButton';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import { ModuleType } from 'infrastructure/enums';
import { useModuleWithLocalApcm } from 'infrastructure/providers/module-provider';
import { FileTextOutlined } from '@ant-design/icons';

import s from './styles.module.scss';
import { columns } from './columns';
import { useClinicNotes } from './hooks/use-clinic-notes';
import ModuleTabs from '../module-tabs';
import ClinicalNotesLoggedModal from '../clinical-notes-logged-modal';

import type { DateRange, SortState } from 'infrastructure/types';
import type { IClinicNotes } from 'infrastructure/interfaces';
import type { TDeleteCommentFn } from 'infrastructure/hooks/rpm';
import type { IModuleTab } from '../module-tabs';

interface IProps {
  patientGuid: string;
  dateRange?: DateRange | [];
  showTabs?: boolean;
  module?: ModuleType;
  showTitle?: boolean;
  allowAdd?: boolean;
}

interface HeaderSectionProps {
  title: string;
  btnLabel: string;
  disabledAdd: boolean;
  showTabs: boolean;
  module?: ModuleType;
  onAddNote: () => void;
  onModuleChange: (tab: IModuleTab) => void;
  showTitle: boolean;
  allowAdd?: boolean;
}

const HeaderSection: FC<HeaderSectionProps> = ({
  title,
  btnLabel,
  disabledAdd,
  showTabs,
  module,
  onAddNote,
  onModuleChange,
  allowAdd,
  showTitle = true,
}) => (
  <div className={s['title-wrapper']}>
    {showTitle && (
      <h3 className={s.title} data-cy="clinical-notes-section">
        {title}
      </h3>
    )}
    <BaseButton
      label={`+ ${btnLabel}`}
      dataCy="addNote-button"
      disabled={disabledAdd}
      onClick={onAddNote}
    />
    {allowAdd && (
      <BaseButton
        label={`+ ${btnLabel}`}
        dataCy="addNote-button"
        disabled={disabledAdd}
        onClick={onAddNote}
      />
    )}
    {showTabs && (
      <ModuleTabs
        activeKey={module}
        className={s.tabs}
        onChange={onModuleChange}
        showApcm
      />
    )}
  </div>
);

interface TableSectionProps {
  clinicalNotes: IClinicNotes[];
  loading: boolean;
  page: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  emptyText: string;
  deleteNote: TDeleteCommentFn;
  documentationTimeSort: SortState;
  setDocumentationTimeSort: (sort: SortState) => void;
  createdAtSort: SortState;
  setCreatedAtSort: (sort: SortState) => void;
  module?: ModuleType;
}

const TableSection: FC<TableSectionProps> = ({
  clinicalNotes,
  loading,
  page,
  totalCount,
  onPageChange,
  emptyText,
  deleteNote,
  documentationTimeSort,
  setDocumentationTimeSort,
  createdAtSort,
  setCreatedAtSort,
  module,
}) => (
  <BaseTableNew<IClinicNotes>
    items={clinicalNotes}
    columns={columns({
      deleteNote,
      documentationTimeSort,
      createdAtSort,
      module,
    })}
    bordered
    loading={loading}
    pagination={{
      current: page,
      pageSize: SMS_ACTIVITIES_PER_PAGE,
      total: totalCount,
      position: ['bottomRight'],
    }}
    onChangePage={onPageChange}
    dataCy="clinic-notes-table"
    emptyText={
      <EmptyTablePlaceholder
        text={emptyText}
        icon={<FileTextOutlined />}
        column
      />
    }
    changeOddRowColor={false}
    rowHoverable={false}
    onSort={(field, order) => {
      if (!field || !order) {
        setCreatedAtSort('');
        setDocumentationTimeSort('');
      } else if (field === 'createdAt') {
        setCreatedAtSort(order);
        setDocumentationTimeSort('');
      } else if (field === 'documentationTime') {
        setDocumentationTimeSort(order);
        setCreatedAtSort('');
      }
    }}
  />
);

const ClinicalNotesLoggedTable: FC<IProps> = ({
  patientGuid,
  module = ModuleType.CCM,
  showTabs,
  dateRange = [],
  showTitle = true,
  allowAdd = true,
}) => {
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const [createdAtSort, setCreatedAtSort] = useState<SortState>('');

  const { module: internalModule, changeModule: setModuleType } =
    useModuleWithLocalApcm(module);

  const moduleType = showTabs ? internalModule : module;

  const {
    clinicalNotes,
    loading,
    disableButton,
    page,
    opened,
    toggleOpen,
    setPage,
    deleteNote,
  } = useClinicNotes({
    patientGuid,
    dateRange,
    module: moduleType,
    documentationTimeSort,
    createdAtSort,
  });

  const { t } = useTranslation();
  const disabledAdd = disableButton || loading;

  const btnLabel = {
    [ModuleType.RPM]: t('labels.addModuleNote', { module: 'RPM' }),
    [ModuleType.CCM]: t('labels.addModuleNote', { module: 'CCM' }),
    [ModuleType.APCM]: t('labels.addModuleNote', { module: 'APCM' }),
    [ModuleType.ADMIN]: '',
    [ModuleType.RPMCCM]: '',
  } satisfies Record<ModuleType, string>;

  const handleModuleChange = (tab: IModuleTab) => {
    setModuleType(tab.key);
    setPage(1);
    setDocumentationTimeSort('');
    setCreatedAtSort('');
  };

  return (
    <>
      <div className={s.wrapper}>
        <HeaderSection
          title={t('labels.clinicalNotesLogged')}
          btnLabel={btnLabel[moduleType]}
          disabledAdd={disabledAdd}
          showTabs={showTabs ?? false}
          onAddNote={toggleOpen}
          onModuleChange={handleModuleChange}
          module={moduleType}
          showTitle={showTitle}
          allowAdd={allowAdd}
        />
        <TableSection
          clinicalNotes={clinicalNotes?.items ?? []}
          loading={loading}
          page={page}
          totalCount={clinicalNotes?.count ?? 0}
          onPageChange={setPage}
          emptyText={t('labels.noClinicalNotesLogged')}
          deleteNote={(record) => deleteNote(record.guid)}
          documentationTimeSort={documentationTimeSort}
          setDocumentationTimeSort={setDocumentationTimeSort}
          createdAtSort={createdAtSort}
          setCreatedAtSort={setCreatedAtSort}
          module={moduleType}
        />
      </div>
      {opened && (
        <ClinicalNotesLoggedModal
          module={moduleType}
          patientGuid={patientGuid}
          open={opened}
          onClose={toggleOpen}
          isInitialTemplateUsed={clinicalNotes?.isInitialTemplateUsed}
        />
      )}
    </>
  );
};

export default ClinicalNotesLoggedTable;
